import { Button, Card, EmptyState, Page, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useModal } from '../../../hooks/use-modal';
import { APISettingsUpsell } from '../components/api-settings-upsell';
import { CreateKeyModal } from '../components/modals/create-key';
import { DeleteConfirmationModal } from '../components/modals/delete-confimation';
import { ShowKeyModal } from '../components/modals/show-key';
import { useAPISettingsKeys } from '../hooks/use-api-settings-keys';
import { useAPISettingsCreate } from '../hooks/use-api-settings-keys-create';

export const APISettingsPage = withPageProviders(APISettingsPageContent);

const API_SETTINGS_LEARN_MORE_URL = 'http://docs.sparklayer.io/dashboard-configurations#jlxcz';

function APISettingsPageContent() {
  const { navigationHandler } = useHandlers();
  const env = useEnv();

  const [keyToDelete, setKeyToDelete] = useState<{ name: string; clientId: string } | null>(null);

  const {
    data: keys,
    isLoading: keysLoading,
    isError: keysError,
    refetch: refetchKeys,
  } = useAPISettingsKeys();

  const { data: createdKey, mutateAsync: createKey, reset: resetKey } = useAPISettingsCreate();

  const {
    isOpen: isCreateKeyModalOpen,
    openModal: openCreateKeyModal,
    closeModal: closeCreateKeyModal,
  } = useModal();

  const {
    isOpen: isShowKeyModalOpen,
    openModal: openShowKeyModal,
    closeModal: closeShowKeyModal,
  } = useModal();

  const {
    isOpen: isDeleteConfirmationModalOpen,
    openModal: openDeleteConfirmationModal,
    closeModal: closeDeleteConfirmationModal,
  } = useModal();

  function handleDelete(clientId: string, name: string) {
    setKeyToDelete({ name: name, clientId: clientId });
    openDeleteConfirmationModal();
  }

  const planHasAPI = !featureRestricted(
    features.API_ACCESS,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  if (!planHasAPI) {
    return (
      <Page
        title={t('features:api_settings.pages.api_settings_page.header.title')}
        description={t('features:api_settings.pages.api_settings_page.header.description')}
        learnMoreUrl={API_SETTINGS_LEARN_MORE_URL}
      >
        <APISettingsUpsell />
      </Page>
    );
  }

  return (
    <Page
      title={t('features:api_settings.pages.api_settings_page.header.title')}
      description={t('features:api_settings.pages.api_settings_page.header.description')}
      learnMoreUrl={API_SETTINGS_LEARN_MORE_URL}
      primaryAction={{
        icon: 'key',
        onClick: openCreateKeyModal,
        label: t('features:api_settings.pages.api_settings_page.header.actions.create'),
      }}
    >
      <SparkSuspense
        isError={keysError}
        isLoading={keysLoading}
        errorMessage={t('errors:generic')}
        onReload={() => void refetchKeys()}
      >
        {keys.length > 0 ? (
          <Table
            headings={[
              t('features:api_settings.pages.api_settings_page.table.headings.name'),
              t('features:api_settings.pages.api_settings_page.table.headings.client_id'),
              t('features:api_settings.pages.api_settings_page.table.headings.created'),
            ]}
            caption={t('features:api_settings.pages.api_settings_page.table.caption')}
          >
            {keys.map((key, i) => (
              <Table.Row key={key.client_id} id={key.client_id} index={i}>
                <Table.Cell>{key.name}</Table.Cell>
                <Table.Cell>{key.client_id}</Table.Cell>
                <Table.Cell>{new Date(key.created_at).toLocaleString()}</Table.Cell>
                <Table.Cell>
                  <Button
                    variant="dangerOutline"
                    icon="delete"
                    onClick={() => handleDelete(key.client_id, key.name)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>
        ) : (
          <Card>
            <EmptyState
              title={t('features:api_settings.pages.api_settings_page.table.empty.title')}
            >
              <Text variant="secondary">
                {t('features:api_settings.pages.api_settings_page.table.empty.body')}
              </Text>
            </EmptyState>
          </Card>
        )}
      </SparkSuspense>
      <Card>
        <Card.Header headingLevel="h3">
          <Text>{t('features:api_settings.pages.api_settings_page.docs.header')}</Text>
        </Card.Header>
        <Card.Body>
          <Text>{t('features:api_settings.pages.api_settings_page.docs.body')}</Text>
          <span className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon="externalLink"
              onClick={() =>
                navigationHandler.navigate('https://docs.sparklayer.io/sparklayer-api', {
                  absolute: true,
                  newTab: true,
                })
              }
            >
              <Text>{t('features:api_settings.pages.api_settings_page.docs.buttons.docs')}</Text>
            </Button>
            <Button
              variant="secondary"
              icon="userAdd"
              onClick={() => navigationHandler.navigate('/settings/team', { excludeSiteEnv: true })}
            >
              <Text>{t('features:api_settings.pages.api_settings_page.docs.buttons.invite')}</Text>
            </Button>
          </span>
        </Card.Body>
      </Card>

      <CreateKeyModal
        createKey={createKey}
        isOpen={isCreateKeyModalOpen}
        closeModal={() => {
          closeCreateKeyModal();
        }}
        openShowKeyModal={openShowKeyModal}
      />

      <ShowKeyModal
        keyData={createdKey}
        isOpen={isShowKeyModalOpen}
        closeModal={() => {
          closeShowKeyModal();
          resetKey();
        }}
      />

      <DeleteConfirmationModal
        keyData={keyToDelete}
        isOpen={isDeleteConfirmationModalOpen}
        closeModal={closeDeleteConfirmationModal}
      />
    </Page>
  );
}
